import { Image } from 'expo-image';
import { Link, router, useLocalSearchParams } from 'expo-router';
import {
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  Share2,
} from 'lucide-react-native';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ActivityIndicator,
  FlatList,
  type LayoutChangeEvent,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import {
  APP_BOTTOM_TAB_BAR_BASE_HEIGHT,
  getStableBottomInset,
} from '@/src/components/AuthenticatedBottomTabBar';
import { AppChromeShell } from '@/src/components/AppChromeShell';
import { Button } from '@/src/components/Button';
import { ErrorView } from '@/src/components/ErrorView';
import { IdentityHeader } from '@/src/components/IdentityHeader';
import { InternalNotFound } from '@/src/components/InternalNotFound';
import { PublicPageShell } from '@/src/components/PublicPageShell';
import { useToast } from '@/src/components/Toast';
import { useAuth } from '@/src/features/auth/use-auth';
import {
  CommentComposer,
  CommentComposerCard,
  type CommentComposerProps,
} from '@/src/features/comments/CommentComposer';
import { COMMENT_MODAL_COMPACT_BREAKPOINT } from '@/src/features/comments/CommentModalShell';
import { CommentSortControl } from '@/src/features/comments/CommentSortControl';
import type {
  CommentReplyTarget,
  CommentSortMode,
} from '@/src/features/comments/comment-threading';
import { ReplyCommentComposer } from '@/src/features/comments/ReplyCommentComposer';
import { CommentThreadModal } from '@/src/features/comments/CommentThreadModal';
import { useCommentComposerState } from '@/src/features/comments/use-comment-composer-state';
import { useCommentSortPreference } from '@/src/features/comments/use-comment-sort-preference';
import { GarageVehicleCommentThreadList } from '@/src/features/garage/GarageVehicleCommentThreadList';
import { GarageVehicleShareModal } from '@/src/features/garage/GarageVehicleShareModal';
import {
  MAX_GARAGE_COMMENT_LENGTH,
  garageVehicleShareURL,
  garageVehicleTitle,
  getPublicGarageVehicle,
  type GarageVehicle,
  type GarageVehicleComment,
  type GarageVehiclePhoto,
} from '@/src/features/garage/garage-service';
import { withLiveGarageVehicleOwnerProfile } from '@/src/features/garage/garage-owner-profile';
import {
  useCreateGarageVehicleComment,
  useGarageVehicleComments,
  useGarageVehiclePhotos,
  useGarageVehicleReaction,
  useRealtimeGarageVehicle,
  useSetGarageVehicleReaction,
} from '@/src/features/garage/use-garage';
import { GiphyPickerModal } from '@/src/features/giphy/GiphyPickerModal';
import {
  applyReactionTransition,
  CompactReactionButton,
  createReactionMutationId,
  ReactionDetailsModal,
  ReactionSummaryButton,
  reactionMeta,
  shouldClearOptimisticReaction,
} from '@/src/features/posts/ReactionControls';
import { getTopUniqueReactions } from '@/src/features/posts/reaction-summary';
import {
  emptyReactionCounts,
  type PostMediaItem,
  type PublicPost,
  type Reaction,
  type ReactionCounts,
} from '@/src/features/posts/post-service';
import { PostPhotoViewerModal } from '@/src/features/posts/PostPhotoViewerModal';
import { getProfileAvatarURL } from '@/src/features/profile/profile-service';
import {
  useProfile,
  usePublicProfile,
} from '@/src/features/profile/use-profile';
import { maybeCorrectWriting } from '@/src/features/writing-assist/writing-assist-service';
import { trackEvent } from '@/src/lib/analytics';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { formatDate } from '@/src/utils/dates';
import { openUserProfile } from '@/src/utils/profile-navigation';

type PublicVehiclePayload = {
  comments: GarageVehicleComment[];
  photos: GarageVehiclePhoto[];
  vehicle: GarageVehicle | null;
};

const GARAGE_COMMENT_VIDEO_MAX_DURATION_SECONDS = 10 * 60;
const GARAGE_COMMENT_VIDEO_MAX_DURATION_MILLIS =
  GARAGE_COMMENT_VIDEO_MAX_DURATION_SECONDS * 1000;

function vehiclePhotoPreviewURL(photo: GarageVehiclePhoto) {
  return photo.thumbnail?.downloadURL ?? photo.downloadURL;
}

function vehiclePhotoOriginalURL(photo: GarageVehiclePhoto) {
  return photo.downloadURL ?? photo.thumbnail?.downloadURL;
}

function CoverSliderImage({
  index,
  photo,
  title,
}: {
  index: number;
  photo: GarageVehiclePhoto;
  title: string;
}) {
  const previewURL = vehiclePhotoPreviewURL(photo);
  const originalURL = vehiclePhotoOriginalURL(photo);
  const [sourceURL, setSourceURL] = useState(previewURL);

  useEffect(() => {
    setSourceURL(previewURL);
  }, [photo.id, previewURL]);

  return (
    <Image
      accessibilityLabel={`${title} photo ${index + 1}`}
      cachePolicy="memory-disk"
      contentFit="cover"
      onError={() => {
        if (sourceURL !== originalURL) {
          setSourceURL(originalURL);
        }
      }}
      source={{ uri: sourceURL }}
      style={styles.coverImage}
    />
  );
}

function orderedGalleryPhotos({
  photos,
  vehicle,
}: {
  photos: GarageVehiclePhoto[];
  vehicle: GarageVehicle;
}) {
  const galleryPhotos =
    photos.length > 0 ? photos : vehicle.coverPhoto ? [vehicle.coverPhoto] : [];

  if (!vehicle.coverPhoto) {
    return galleryPhotos;
  }

  const coverIndex = galleryPhotos.findIndex(
    (photo) => photo.id === vehicle.coverPhoto?.id,
  );

  if (coverIndex <= 0) {
    return galleryPhotos;
  }

  return [
    galleryPhotos[coverIndex],
    ...galleryPhotos.slice(0, coverIndex),
    ...galleryPhotos.slice(coverIndex + 1),
  ];
}

function garagePhotoMediaItem(
  photo: GarageVehiclePhoto,
  index: number,
): PostMediaItem {
  return {
    id: photo.id,
    kind: 'image',
    order: index,
    storagePath: photo.storagePath,
    downloadURL: vehiclePhotoOriginalURL(photo),
    contentType: photo.contentType,
    sizeBytes: photo.sizeBytes,
    width: photo.width,
    height: photo.height,
    durationMillis: null,
    thumbnail: photo.thumbnail,
    thumbnailStatus: photo.thumbnailStatus,
    thumbnailError: photo.thumbnailError,
  };
}

function garageViewerDate(
  value: GarageVehicle['updatedAt'],
): PublicPost['createdAt'] {
  if (typeof value !== 'string') {
    return value;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

function garagePhotoViewerPost({
  photos,
  vehicle,
}: {
  photos: GarageVehiclePhoto[];
  vehicle: GarageVehicle;
}): PublicPost {
  const mediaItems = photos.map(garagePhotoMediaItem);
  const firstMedia = mediaItems[0] ?? null;
  const updatedAt = garageViewerDate(vehicle.updatedAt);

  return {
    id: `garage:${vehicle.id}`,
    authorUid: vehicle.ownerUid,
    authorFullName: vehicle.ownerFullName,
    authorUsername: vehicle.ownerUsername,
    authorPhotoURL: vehicle.ownerPhotoURL,
    authorFounderBadge: vehicle.ownerFounderBadge === true,
    authorVerifiedBadge: vehicle.ownerVerifiedBadge === true,
    body: vehicle.story,
    visibility: 'public',
    media: firstMedia
      ? {
          kind: firstMedia.kind,
          storagePath: firstMedia.storagePath,
          downloadURL: firstMedia.downloadURL,
          contentType: firstMedia.contentType,
          sizeBytes: firstMedia.sizeBytes,
          width: firstMedia.width,
          height: firstMedia.height,
          durationMillis: firstMedia.durationMillis,
          thumbnail: firstMedia.thumbnail,
          thumbnailStatus: firstMedia.thumbnailStatus,
          thumbnailError: firstMedia.thumbnailError,
        }
      : null,
    mediaItems,
    linkCard: null,
    linkPreview: null,
    reshare: null,
    feedStats: {
      commentOpenCount: 0,
      detailOpenCount: 0,
      dwellBucketTotal: 0,
      impressionCount: 0,
    },
    likeCount: 0,
    reactionCounts: emptyReactionCounts(),
    reactionCount: 0,
    commentCount: vehicle.commentCount,
    shareCount: 0,
    profileMentions: [],
    createdAt: updatedAt,
    editCount: 0,
    editedAt: null,
    updatedAt,
  };
}

type OptimisticGarageReactionState = {
  baseCounts: ReactionCounts;
  baseReaction: Reaction | null;
  mutationId: string;
  reaction: Reaction | null;
};

function GaragePhotoViewerActions({
  onCloseViewer,
  onCommentPress,
  shareURL,
  userUid,
  vehicle,
}: {
  onCloseViewer: () => void;
  onCommentPress?: () => void;
  shareURL: string | null;
  userUid?: string;
  vehicle: GarageVehicle;
}) {
  const { showError } = useToast();
  const reactionState = useGarageVehicleReaction(vehicle.id, userUid);
  const setReaction = useSetGarageVehicleReaction();
  const [optimisticReaction, setOptimisticReaction] =
    useState<OptimisticGarageReactionState | null>(null);
  const [shareMenuVisible, setShareMenuVisible] = useState(false);
  const [reactionDetailsVisible, setReactionDetailsVisible] = useState(false);
  const baseReactionCounts = vehicle.reactionCounts ?? emptyReactionCounts();
  const optimisticCounts = optimisticReaction
    ? applyReactionTransition(optimisticReaction)
    : null;
  const selectedReaction = optimisticReaction
    ? optimisticReaction.reaction
    : reactionState.reaction;
  const visibleReactionCounts = optimisticCounts?.counts ?? baseReactionCounts;
  const totalReactions = Object.values(visibleReactionCounts).reduce(
    (total, count) => total + count,
    0,
  );
  const topReactionIcons = getTopUniqueReactions(visibleReactionCounts);
  const canShareOnFrienzzi = Boolean(userUid && userUid === vehicle.ownerUid);

  useEffect(() => {
    setOptimisticReaction(null);
  }, [userUid, vehicle.id]);

  useEffect(() => {
    if (!optimisticReaction) {
      return;
    }

    if (
      optimisticCounts &&
      shouldClearOptimisticReaction({
        currentCounts: baseReactionCounts,
        currentMutationAck: reactionState.mutationId,
        expectedCounts: optimisticCounts.counts,
        startingCounts: optimisticReaction.baseCounts,
        persistedReaction: reactionState.reaction,
        targetMutationId: optimisticReaction.mutationId,
        targetReaction: optimisticReaction.reaction,
      })
    ) {
      setOptimisticReaction(null);
    }
  }, [
    baseReactionCounts,
    optimisticCounts,
    optimisticReaction,
    reactionState.mutationId,
    reactionState.reaction,
  ]);

  function selectReaction(reaction: Reaction) {
    if (!userUid) {
      onCloseViewer();
      router.push('/sign-in');
      return;
    }

    const nextReaction = selectedReaction === reaction ? null : reaction;
    const mutationId = createReactionMutationId();
    setOptimisticReaction({
      baseCounts: optimisticCounts?.counts ?? baseReactionCounts,
      baseReaction: selectedReaction,
      mutationId,
      reaction: nextReaction,
    });
    setReaction.mutate(
      { mutationId, reaction: nextReaction, vehicleId: vehicle.id },
      {
        onError: (error) => {
          setOptimisticReaction(null);
          showError(error, 'Unable to update that vehicle reaction.');
        },
        onSuccess: () => {
          trackEvent('garage_vehicle_reaction_set', {
            reaction: nextReaction ?? 'none',
            source: 'garage_photo_viewer',
          });
        },
      },
    );
  }

  function openComments() {
    onCloseViewer();
    requestAnimationFrame(() => {
      onCommentPress?.();
    });
  }

  function openShareMenu() {
    if (!shareURL) {
      showError(
        new Error('Missing Garage URL.'),
        'Unable to share this Garage vehicle.',
      );
      return;
    }

    setShareMenuVisible(true);
  }

  return (
    <View style={styles.viewerActionsRail}>
      <View style={styles.viewerActionCluster}>
        <View style={styles.viewerRailIconCircle}>
          <CompactReactionButton
            disabled={reactionState.loading}
            loading={false}
            pickerOpensLeft
            selectedReaction={selectedReaction}
            onSelect={selectReaction}
          />
        </View>
        <Text style={styles.viewerActionCount}>{totalReactions}</Text>
      </View>
      <Pressable
        accessibilityLabel="View Garage comments"
        accessibilityRole="button"
        onPress={openComments}
        style={({ pressed }) => [
          styles.viewerRailButton,
          pressed ? styles.viewerRailButtonPressed : null,
        ]}
      >
        <View style={styles.viewerRailIconCircle}>
          <MessageCircle color={colors.surface} size={25} strokeWidth={2.4} />
        </View>
        <Text style={styles.viewerActionCount}>{vehicle.commentCount}</Text>
      </Pressable>
      <Pressable
        accessibilityLabel="Share Garage vehicle"
        accessibilityRole="button"
        disabled={!shareURL}
        onPress={openShareMenu}
        style={({ pressed }) => [
          styles.viewerRailButton,
          pressed ? styles.viewerRailButtonPressed : null,
          !shareURL ? styles.viewerRailButtonDisabled : null,
        ]}
      >
        <View style={styles.viewerRailIconCircle}>
          <Share2 color={colors.surface} size={24} strokeWidth={2.4} />
        </View>
      </Pressable>
      {totalReactions > 0 ? (
        <Pressable
          accessibilityLabel="View Garage reactions"
          accessibilityRole="button"
          onPress={() => {
            if (!userUid) {
              onCloseViewer();
              router.push('/sign-in');
              return;
            }

            setReactionDetailsVisible(true);
          }}
          style={({ pressed }) => [
            styles.viewerTopReactionButton,
            pressed ? styles.viewerRailButtonPressed : null,
          ]}
        >
          {topReactionIcons.map((reaction, index) => (
            <View
              key={`${reaction}-${index}`}
              style={[
                styles.viewerTopReactionBubble,
                index > 0 ? styles.viewerTopReactionBubbleOverlap : null,
              ]}
            >
              <Text style={styles.viewerTopReactionEmoji}>
                {reactionMeta[reaction].emoji}
              </Text>
            </View>
          ))}
        </Pressable>
      ) : null}
      <GarageVehicleShareModal
        canShareOnFrienzzi={canShareOnFrienzzi}
        source="garage_detail"
        vehicle={vehicle}
        visible={shareMenuVisible}
        onClose={() => setShareMenuVisible(false)}
      />
      <ReactionDetailsModal
        counts={visibleReactionCounts}
        currentUid={userUid}
        target={{ kind: 'vehicle', vehicleId: vehicle.id }}
        visible={reactionDetailsVisible}
        onClose={() => setReactionDetailsVisible(false)}
      />
    </View>
  );
}

function VehiclePhotoSlider({
  currentUid,
  onCommentPress,
  shareURL,
  vehicle,
  photos,
  title,
}: {
  currentUid?: string;
  onCommentPress?: () => void;
  shareURL: string | null;
  vehicle: GarageVehicle;
  photos: GarageVehiclePhoto[];
  title: string;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [viewerVisible, setViewerVisible] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  useEffect(() => {
    setActiveIndex((currentIndex) =>
      Math.min(currentIndex, Math.max(photos.length - 1, 0)),
    );
    setViewerIndex((currentIndex) =>
      Math.min(currentIndex, Math.max(photos.length - 1, 0)),
    );
  }, [photos.length]);

  if (photos.length === 0) {
    return null;
  }

  const hasMultiplePhotos = photos.length > 1;

  function scrollSliderToIndex(nextIndex: number) {
    const clampedIndex = Math.min(Math.max(nextIndex, 0), photos.length - 1);

    setActiveIndex(clampedIndex);
  }

  function setViewerToIndex(nextIndex: number) {
    const clampedIndex = Math.min(Math.max(nextIndex, 0), photos.length - 1);

    setViewerIndex(clampedIndex);
  }

  function openViewer(index: number) {
    const clampedIndex = Math.min(Math.max(index, 0), photos.length - 1);

    setViewerIndex(clampedIndex);
    setViewerVisible(true);
  }

  function closeViewer() {
    setViewerVisible(false);
    scrollSliderToIndex(viewerIndex);
  }

  const activePhoto = photos[activeIndex] ?? photos[0];
  const viewerPost = garagePhotoViewerPost({ photos, vehicle });

  return (
    <>
      <Pressable
        accessibilityLabel={`Open ${title} photo ${activeIndex + 1}`}
        accessibilityRole="button"
        onPress={() => openViewer(activeIndex)}
        style={styles.coverSlider}
      >
        <CoverSliderImage
          index={activeIndex}
          photo={activePhoto}
          title={title}
        />
        {hasMultiplePhotos ? (
          <>
            <Pressable
              accessibilityLabel="Previous vehicle photo"
              accessibilityRole="button"
              onPress={(event) => {
                event.stopPropagation();
                scrollSliderToIndex(activeIndex - 1);
              }}
              style={({ pressed }) => [
                styles.photoArrow,
                styles.photoArrowLeft,
                pressed ? styles.photoArrowPressed : null,
              ]}
            >
              <ChevronLeft color={colors.surface} size={24} strokeWidth={2.7} />
            </Pressable>
            <Pressable
              accessibilityLabel="Next vehicle photo"
              accessibilityRole="button"
              onPress={(event) => {
                event.stopPropagation();
                scrollSliderToIndex(activeIndex + 1);
              }}
              style={({ pressed }) => [
                styles.photoArrow,
                styles.photoArrowRight,
                pressed ? styles.photoArrowPressed : null,
              ]}
            >
              <ChevronRight
                color={colors.surface}
                size={24}
                strokeWidth={2.7}
              />
            </Pressable>
          </>
        ) : null}
        <View style={styles.photoCountPill}>
          <Text style={styles.photoCountText}>
            {activeIndex + 1} / {photos.length}
          </Text>
        </View>
        {hasMultiplePhotos ? (
          <View style={styles.photoDots}>
            {photos.map((photo, index) => (
              <View
                key={photo.id}
                style={[
                  styles.photoDot,
                  index === activeIndex ? styles.activePhotoDot : null,
                ]}
              />
            ))}
          </View>
        ) : null}
      </Pressable>
      <PostPhotoViewerModal
        initialPhotoIndex={viewerIndex}
        initialPost={viewerPost}
        initialPostId={viewerPost.id}
        posts={[viewerPost]}
        actionRail={
          <GaragePhotoViewerActions
            onCloseViewer={closeViewer}
            onCommentPress={onCommentPress}
            shareURL={shareURL}
            userUid={currentUid}
            vehicle={vehicle}
          />
        }
        showPostActions={false}
        visible={viewerVisible}
        onActivePhotoIndexChange={(nextPhotoIndex) => {
          setViewerToIndex(nextPhotoIndex);
        }}
        onClose={closeViewer}
        onCommentPress={() => undefined}
      />
    </>
  );
}

function VehicleHeader({
  currentUid,
  engagement,
  onViewerCommentPress,
  photos,
  shareURL,
  vehicle,
}: {
  currentUid?: string;
  engagement?: ReactNode;
  onViewerCommentPress?: () => void;
  photos: GarageVehiclePhoto[];
  shareURL: string | null;
  vehicle: GarageVehicle;
}) {
  const galleryPhotos = orderedGalleryPhotos({ photos, vehicle });
  const title = garageVehicleTitle(vehicle);

  return (
    <View style={styles.vehicleCard}>
      <View style={styles.coverFrame}>
        {galleryPhotos.length > 0 ? (
          <VehiclePhotoSlider
            currentUid={currentUid}
            onCommentPress={onViewerCommentPress}
            photos={galleryPhotos}
            shareURL={shareURL}
            title={title}
            vehicle={vehicle}
          />
        ) : (
          <View style={styles.coverPlaceholder}>
            <Text style={styles.coverPlaceholderText}>Garage</Text>
          </View>
        )}
      </View>
      <View style={styles.vehicleContent}>
        <IdentityHeader
          avatarSize={44}
          containerStyle={styles.ownerHeader}
          currentUid={currentUid}
          founderBadge={vehicle.ownerFounderBadge}
          fullName={vehicle.ownerFullName}
          metadata={[
            {
              id: 'garage',
              kind: 'text',
              label: 'Garage',
            },
          ]}
          photoURL={vehicle.ownerPhotoURL}
          uid={vehicle.ownerUid}
          username={vehicle.ownerUsername}
          verifiedBadge={vehicle.ownerVerifiedBadge}
          onPress={() => {
            if (!currentUid) {
              router.push('/sign-in');
              return;
            }

            openUserProfile({
              currentUid,
              targetUid: vehicle.ownerUid,
            });
          }}
        />
        <Text style={styles.vehicleTitle}>{title}</Text>
        {vehicle.trim ? (
          <Text style={styles.subtitle}>{vehicle.trim}</Text>
        ) : null}
        <View style={styles.metaRow}>
          <Text style={styles.metaText}>{vehicle.ownershipStatus}</Text>
          <Text style={styles.metaDot}>·</Text>
          <Text style={styles.metaText}>{vehicle.photoCount} photos</Text>
          <Text style={styles.metaDot}>·</Text>
          <Text style={styles.metaText}>
            Updated {formatDate(vehicle.updatedAt)}
          </Text>
        </View>
        {vehicle.story ? (
          <Text style={styles.storyText}>{vehicle.story}</Text>
        ) : null}
        {engagement}
      </View>
    </View>
  );
}

function VehicleEngagement({
  onCommentPress,
  shareURL,
  userUid,
  vehicle,
}: {
  onCommentPress?: () => void;
  shareURL: string | null;
  userUid?: string;
  vehicle: GarageVehicle;
}) {
  const { showError } = useToast();
  const reactionState = useGarageVehicleReaction(vehicle.id, userUid);
  const setReaction = useSetGarageVehicleReaction();
  const [optimisticReaction, setOptimisticReaction] =
    useState<OptimisticGarageReactionState | null>(null);
  const [shareMenuVisible, setShareMenuVisible] = useState(false);
  const baseReactionCounts = vehicle.reactionCounts ?? emptyReactionCounts();
  const optimisticCounts = optimisticReaction
    ? applyReactionTransition(optimisticReaction)
    : null;
  const selectedReaction = optimisticReaction
    ? optimisticReaction.reaction
    : reactionState.reaction;
  const visibleReactionCounts = optimisticCounts?.counts ?? baseReactionCounts;
  const totalReactions = Object.values(visibleReactionCounts).reduce(
    (total, count) => total + count,
    0,
  );
  const canShareOnFrienzzi = Boolean(userUid && userUid === vehicle.ownerUid);

  useEffect(() => {
    setOptimisticReaction(null);
  }, [userUid, vehicle.id]);

  useEffect(() => {
    if (!optimisticReaction) {
      return;
    }

    if (
      optimisticCounts &&
      shouldClearOptimisticReaction({
        currentCounts: baseReactionCounts,
        currentMutationAck: reactionState.mutationId,
        expectedCounts: optimisticCounts.counts,
        startingCounts: optimisticReaction.baseCounts,
        persistedReaction: reactionState.reaction,
        targetMutationId: optimisticReaction.mutationId,
        targetReaction: optimisticReaction.reaction,
      })
    ) {
      setOptimisticReaction(null);
    }
  }, [
    baseReactionCounts,
    optimisticCounts,
    optimisticReaction,
    reactionState.mutationId,
    reactionState.reaction,
  ]);

  function selectReaction(reaction: Reaction) {
    if (!userUid) {
      router.push('/sign-in');
      return;
    }

    const nextReaction = selectedReaction === reaction ? null : reaction;
    const mutationId = createReactionMutationId();
    setOptimisticReaction({
      baseCounts: optimisticCounts?.counts ?? baseReactionCounts,
      baseReaction: selectedReaction,
      mutationId,
      reaction: nextReaction,
    });
    setReaction.mutate(
      { mutationId, reaction: nextReaction, vehicleId: vehicle.id },
      {
        onSuccess: () => {
          trackEvent('garage_vehicle_reaction_set', {
            reaction: nextReaction ?? 'none',
            source: 'garage_detail',
          });
        },
        onError: (error) => {
          setOptimisticReaction(null);
          showError(error, 'Unable to update that vehicle reaction.');
        },
      },
    );
  }

  function openShareMenu() {
    if (!shareURL) {
      showError(
        new Error('Missing Garage URL.'),
        'Unable to share this Garage vehicle.',
      );
      return;
    }

    setShareMenuVisible(true);
  }

  return (
    <View style={styles.engagementRow}>
      <View style={styles.engagementLeft}>
        <CompactReactionButton
          disabled={reactionState.loading}
          loading={false}
          selectedReaction={selectedReaction}
          onSelect={selectReaction}
        />
        {totalReactions > 0 ? (
          <Text style={[styles.commentCountText, styles.reactionCountText]}>
            {totalReactions}
          </Text>
        ) : null}
        <Pressable
          accessibilityLabel="Jump to comments"
          accessibilityRole="button"
          onPress={onCommentPress}
          style={({ pressed }) => [
            styles.commentCount,
            styles.commentButton,
            pressed ? styles.commentButtonPressed : null,
          ]}
        >
          <MessageCircle color={colors.mutedText} size={22} strokeWidth={2.2} />
          <Text style={styles.commentCountText}>{vehicle.commentCount}</Text>
        </Pressable>
        <Pressable
          accessibilityLabel="Share Garage vehicle"
          accessibilityRole="button"
          disabled={!shareURL}
          onPress={() => {
            openShareMenu();
          }}
          style={({ pressed }) => [
            styles.shareButton,
            pressed ? styles.shareButtonPressed : null,
            !shareURL ? styles.shareButtonDisabled : null,
          ]}
        >
          <Share2 color={colors.mutedText} size={22} strokeWidth={2.2} />
        </Pressable>
      </View>
      <ReactionSummaryButton
        counts={visibleReactionCounts}
        currentUid={userUid}
        selectedReaction={selectedReaction}
        target={{ kind: 'vehicle', vehicleId: vehicle.id }}
      />
      <GarageVehicleShareModal
        canShareOnFrienzzi={canShareOnFrienzzi}
        source="garage_detail"
        vehicle={vehicle}
        visible={shareMenuVisible}
        onClose={() => setShareMenuVisible(false)}
      />
    </View>
  );
}

function GarageModsPanel({ vehicle }: { vehicle: GarageVehicle }) {
  if (vehicle.mods.length === 0) {
    return null;
  }

  return (
    <View style={styles.modsPanel}>
      <View style={styles.modsHeader}>
        <Text style={styles.heading}>Mods</Text>
      </View>
      {vehicle.mods.map((mod) => (
        <View key={mod.id} style={styles.modRow}>
          <Text style={styles.modTitle}>{mod.title}</Text>
          {mod.brandPart ? (
            <Text style={styles.modMeta}>{mod.brandPart}</Text>
          ) : null}
          {mod.notes ? <Text style={styles.modText}>{mod.notes}</Text> : null}
        </View>
      ))}
    </View>
  );
}

function VehicleComments({
  comments,
  composer,
  currentUid,
  onLayout,
  onReply,
  onSortModeChange,
  replyComposer,
  replyTarget,
  sortMode,
  vehicle,
}: {
  comments: GarageVehicleComment[];
  composer?: ReactNode;
  currentUid?: string;
  onLayout?: (event: LayoutChangeEvent) => void;
  onReply?: (target: CommentReplyTarget) => void;
  onSortModeChange?: (sortMode: CommentSortMode) => void;
  replyComposer?: ReactNode;
  replyTarget?: CommentReplyTarget | null;
  sortMode?: CommentSortMode;
  vehicle: GarageVehicle;
}) {
  return (
    <View onLayout={onLayout} style={styles.commentsPanel}>
      <View style={styles.commentsHeader}>
        <View style={styles.commentsTitleRow}>
          <Text style={styles.heading}>Comments</Text>
          <View
            style={[
              styles.commentCountBadge,
              comments.length === 0 ? styles.commentCountBadgeEmpty : null,
            ]}
          >
            <Text
              style={[
                styles.commentSectionCountText,
                comments.length === 0 ? styles.commentCountTextEmpty : null,
              ]}
            >
              {comments.length}
            </Text>
          </View>
        </View>
        {sortMode && onSortModeChange ? (
          <CommentSortControl
            compact
            value={sortMode}
            onChange={onSortModeChange}
          />
        ) : null}
      </View>
      {composer}
      {comments.length > 0 ? (
        <GarageVehicleCommentThreadList
          comments={comments}
          currentUid={currentUid}
          replyComposer={replyComposer}
          replyTarget={replyTarget}
          sortMode={sortMode}
          vehicle={vehicle}
          onReply={onReply}
        />
      ) : (
        <View style={styles.emptyState}>
          <Text style={styles.emptyTitle}>No comments yet</Text>
          <Text style={styles.emptyText}>
            Be the first to comment on this Garage vehicle.
          </Text>
        </View>
      )}
      {!currentUid ? (
        <View style={styles.signInCta}>
          <Text style={styles.signInTitle}>Join the conversation</Text>
          <Text style={styles.signInText}>
            Sign in or create an account to comment, react, follow, and share on
            Frienzzi.
          </Text>
          <Link href="/sign-up" asChild>
            <Button title="Join Frienzzi" />
          </Link>
        </View>
      ) : null}
    </View>
  );
}

function SignedInGaragePage({
  initial,
  sourceUsername,
}: {
  initial: PublicVehiclePayload;
  sourceUsername: string;
}) {
  const { user } = useAuth();
  const listRef = useRef<FlatList<GarageVehicleComment>>(null);
  const commentsPanelOffsetRef = useRef(0);
  const { showError, showSuccess } = useToast();
  const commentComposerState = useCommentComposerState({
    maxVideoDurationMillis: GARAGE_COMMENT_VIDEO_MAX_DURATION_MILLIS,
    maxVideoDurationSeconds: GARAGE_COMMENT_VIDEO_MAX_DURATION_SECONDS,
    mediaPasteEventName: 'garage_comment_media_paste',
    showError,
  });
  const {
    attachMedia,
    attachPastedImage,
    beginReply: beginComposerReply,
    body: commentBody,
    canSubmit: canSubmitComment,
    clearMedia,
    closeGiphy,
    giphyGif,
    giphyVisible,
    handleComposerFocusChange,
    inputRef: commentInputRef,
    openGiphy,
    profileMentions: commentMentions,
    replyTarget,
    resetComposer: resetCommentComposer,
    selectGiphy,
    setBody: setCommentBody,
    setProfileMentions: setCommentMentions,
    setReplyTarget,
    setUploadStatus: setCommentUploadStatus,
    uploadMedia,
    uploadStatus: commentUploadStatus,
  } = commentComposerState;
  const [commentModalVisible, setCommentModalVisible] = useState(false);
  const insets = useSafeAreaInsets();
  const { width: viewportWidth } = useWindowDimensions();
  const profileQuery = useProfile(user?.uid);
  const vehicleState = useRealtimeGarageVehicle(initial.vehicle?.id);
  const photosState = useGarageVehiclePhotos(initial.vehicle?.id);
  const commentsState = useGarageVehicleComments(initial.vehicle?.id, user?.uid);
  const createComment = useCreateGarageVehicleComment();
  const { setSortMode, sortMode } = useCommentSortPreference();
  const baseVehicle = vehicleState.vehicle ?? initial.vehicle;
  const ownerProfileQuery = usePublicProfile(
    baseVehicle?.ownerUid,
    Boolean(baseVehicle?.ownerUid),
  );
  const vehicle = useMemo(
    () =>
      withLiveGarageVehicleOwnerProfile(
        baseVehicle,
        ownerProfileQuery.data,
      ),
    [baseVehicle, ownerProfileQuery.data],
  );
  const photos =
    photosState.photos.length > 0 ? photosState.photos : initial.photos;
  const comments =
    commentsState.comments.length > 0
      ? commentsState.comments
      : initial.comments;

  if (!vehicle) {
    return (
      <AppChromeShell fixedBottomTabs>
        <View style={styles.appNotFoundContent}>
          <InternalNotFound message="This Garage vehicle may have moved, been deleted, or is no longer available." />
        </View>
      </AppChromeShell>
    );
  }

  const shareURL = garageVehicleShareURL(vehicle);
  const vehicleId = vehicle.id;
  const garageBackPath =
    vehicle.ownerUid === user?.uid
      ? '/profile?tab=garage'
      : `/profile/${encodeURIComponent(
          vehicle.ownerUsername ?? sourceUsername,
        )}?tab=garage`;
  const fullName =
    profileQuery.data?.fullName || user?.email || 'Frienzzi user';
  const avatarInitial = fullName.slice(0, 1).toUpperCase();
  const avatarURL = getProfileAvatarURL(profileQuery.data, 'thumb');
  const signedInListPaddingBottom =
    APP_BOTTOM_TAB_BAR_BASE_HEIGHT +
    getStableBottomInset(insets.bottom) +
    spacing.xl;
  const compactCommentModal = viewportWidth < COMMENT_MODAL_COMPACT_BREAKPOINT;

  function beginReply(target: CommentReplyTarget) {
    beginComposerReply(target, {
      beforeFocus: () => {
        if (compactCommentModal) {
          setCommentModalVisible(true);
        }
      },
      focusDelay: compactCommentModal ? 120 : 80,
    });
  }

  function closeCommentModal() {
    setCommentModalVisible(false);
    if (compactCommentModal) {
      resetCommentComposer();
    }
  }

  async function submitComment(closeAfterSubmit = false) {
    if (!user?.uid) {
      router.push('/sign-in');
      return;
    }

    if (!canSubmitComment || commentUploadStatus || createComment.isPending) {
      return;
    }

    let body = commentBody.trim();
    const uploadLabel =
      uploadMedia?.kind === 'image'
        ? 'photo'
        : uploadMedia?.kind === 'video'
          ? 'video'
          : null;

    try {
      setCommentUploadStatus(
        uploadLabel ? `Preparing ${uploadLabel}...` : null,
      );

      if (body) {
        setCommentUploadStatus('Checking spelling...');

        try {
          const correction = await maybeCorrectWriting({
            profile: profileQuery.data,
            surface: 'comment',
            text: body,
          });

          body = correction.text;
        } catch {
          // Writing assist is best-effort; users should still be able to post.
        }

        setCommentUploadStatus(
          uploadLabel ? `Preparing ${uploadLabel}...` : null,
        );
      }

      createComment.mutate(
        {
          authorFounderBadge: profileQuery.data?.founderBadge === true,
          authorFullName: fullName,
          authorPhotoURL: avatarURL,
          authorUsername: profileQuery.data?.username ?? null,
          authorVerifiedBadge:
            profileQuery.data?.verification?.badge?.verified === true,
          authorUid: user.uid,
          body,
          giphyGif,
          mediaAsset: uploadMedia?.asset ?? null,
          mediaKind: uploadMedia?.kind ?? null,
          parentCommentId: replyTarget?.parentCommentId ?? null,
          profileMentions: commentMentions,
          replyToCommentId: replyTarget?.replyToCommentId ?? null,
          vehicleId,
        },
        {
          onError: (error) => {
            showError(error, 'Unable to post that Garage comment.');
          },
          onSuccess: () => {
            trackEvent('garage_comment_create', {
              has_gif: Boolean(giphyGif),
              has_media: Boolean(uploadMedia),
              has_text: Boolean(body.trim()),
              media_kind: uploadMedia?.kind ?? (giphyGif ? 'gif' : 'text'),
              reply: Boolean(replyTarget),
            });
            resetCommentComposer();
            showSuccess('Your comment is posting.', 'Garage updated');
            if (closeAfterSubmit) {
              setCommentModalVisible(false);
            }
            setTimeout(() => {
              listRef.current?.scrollToOffset({
                animated: true,
                offset: Math.max(commentsPanelOffsetRef.current - spacing.md, 0),
              });
            }, 120);
          },
        },
      );
    } catch (error) {
      showError(error, 'Unable to post that Garage comment.');
    } finally {
      setCommentUploadStatus(null);
    }
  }

  const garageCommentComposerProps: Omit<CommentComposerProps, 'compact'> = {
    avatarInitial,
    avatarURL,
    body: commentBody,
    canSubmit: canSubmitComment,
    currentUid: user?.uid,
    giphyGif,
    inputRef: commentInputRef,
    loading: Boolean(commentUploadStatus || createComment.isPending),
    maxLength: MAX_GARAGE_COMMENT_LENGTH,
    mentionScope: { kind: 'vehicle_comment', vehicleId },
    profileMentions: commentMentions,
    uploadMedia,
    uploadStatus: commentUploadStatus,
    onAttachPhoto: () => {
      void attachMedia('image');
    },
    onAttachVideo: () => {
      void attachMedia('video');
    },
    onChangeBody: setCommentBody,
    onChangeFocus: handleComposerFocusChange,
    onChangeMentions: setCommentMentions,
    onClearMedia: clearMedia,
    onOpenGiphy: openGiphy,
    onPasteImageAsset: attachPastedImage,
    onSubmit: () => {
      void submitComment();
    },
  };
  const garageCommentComposer = (
    <CommentComposer {...garageCommentComposerProps} compact />
  );
  const replyComposer = replyTarget ? (
    <ReplyCommentComposer
      {...garageCommentComposerProps}
      compact={compactCommentModal}
      replyTarget={replyTarget}
      onCancelReply={() => setReplyTarget(null)}
    />
  ) : null;
  const commentModalPreview = (
    <View style={styles.commentModalPreview}>
      <View style={styles.commentModalPreviewAvatar}>
        {vehicle.ownerPhotoURL ? (
          <Image
            accessibilityLabel=""
            cachePolicy="memory-disk"
            contentFit="cover"
            source={{ uri: vehicle.ownerPhotoURL }}
            style={styles.commentModalPreviewAvatarImage}
          />
        ) : (
          <Text style={styles.commentModalPreviewAvatarText}>
            {(vehicle.ownerFullName ?? 'F').slice(0, 1).toUpperCase()}
          </Text>
        )}
      </View>
      <View style={styles.commentModalPreviewText}>
        <Text numberOfLines={1} style={styles.commentModalAuthor}>
          {garageVehicleTitle(vehicle)}
        </Text>
        <Text numberOfLines={2} style={styles.commentModalBody}>
          {vehicle.ownerFullName
            ? `${vehicle.ownerFullName}'s Garage vehicle`
            : 'Reply to this Garage vehicle.'}
        </Text>
      </View>
    </View>
  );

  return (
    <>
      <AppChromeShell
        backFallbackPath={garageBackPath}
        fixedBottomTabs
        forceBackFallback
      >
        <View style={styles.signedInContent}>
          <FlatList
            ref={listRef}
            style={styles.list}
            contentContainerStyle={[
              styles.listContent,
              { paddingBottom: signedInListPaddingBottom },
            ]}
            data={comments}
            keyExtractor={(comment) => comment.id}
            ListHeaderComponent={
              <>
                <VehicleHeader
                  currentUid={user?.uid}
                  engagement={
                    <VehicleEngagement
                      onCommentPress={() => setCommentModalVisible(true)}
                      shareURL={shareURL}
                      userUid={user?.uid}
                      vehicle={vehicle}
                    />
                  }
                  onViewerCommentPress={() => setCommentModalVisible(true)}
                  photos={photos}
                  shareURL={shareURL}
                  vehicle={vehicle}
                />
                <GarageModsPanel vehicle={vehicle} />
                <VehicleComments
                  composer={
                    replyTarget ? null : (
                      <CommentComposerCard>
                        {garageCommentComposer}
                      </CommentComposerCard>
                    )
                  }
                  comments={comments}
                  currentUid={user?.uid}
                  onSortModeChange={setSortMode}
                  onReply={beginReply}
                  replyComposer={replyComposer}
                  replyTarget={replyTarget}
                  sortMode={sortMode}
                  onLayout={(event) => {
                    commentsPanelOffsetRef.current = event.nativeEvent.layout.y;
                  }}
                  vehicle={vehicle}
                />
              </>
            }
            renderItem={() => null}
          />
        </View>
      </AppChromeShell>
      <CommentThreadModal
        closeAccessibilityLabel="Close Garage comment composer"
        closeDisabled={false}
        closeOnBackdropPress
        commentCount={comments.length}
        compact={compactCommentModal}
        composer={replyTarget ? null : garageCommentComposer}
        emptyText="Be the first to comment on this Garage vehicle."
        preview={commentModalPreview}
        sortMode={sortMode}
        visible={commentModalVisible}
        onChangeSortMode={setSortMode}
        onClose={closeCommentModal}
      >
        <GarageVehicleCommentThreadList
          comments={comments}
          currentUid={user?.uid}
          orderSessionKey={`${vehicle.id}:modal:${
            commentModalVisible ? 'open' : 'closed'
          }`}
          replyComposer={replyComposer}
          replyTarget={replyTarget}
          sortMode={sortMode}
          vehicle={vehicle}
          onReply={beginReply}
        />
      </CommentThreadModal>
      <GiphyPickerModal
        visible={giphyVisible}
        onClose={closeGiphy}
        onSelect={selectGiphy}
      />
    </>
  );
}

export default function PublicGarageVehicleScreen() {
  const params = useLocalSearchParams<{
    username?: string;
    vehicleSlug?: string;
  }>();
  const username =
    typeof params.username === 'string' ? params.username.toLowerCase() : '';
  const vehicleSlug =
    typeof params.vehicleSlug === 'string'
      ? params.vehicleSlug.toLowerCase()
      : '';
  const { initializing, user } = useAuth();
  const publicScrollRef = useRef<ScrollView | null>(null);
  const [publicCommentsOffset, setPublicCommentsOffset] = useState(0);
  const [data, setData] = useState<PublicVehiclePayload | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const { setSortMode, sortMode } = useCommentSortPreference();

  useEffect(() => {
    let active = true;

    if (initializing) {
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setError(null);

    void getPublicGarageVehicle({
      includeComments: !user?.uid,
      username,
      vehicleSlug,
    })
      .then((payload) => {
        if (active) {
          setData(payload);
        }
      })
      .catch((caughtError) => {
        if (active) {
          setError(
            caughtError instanceof Error
              ? caughtError
              : new Error('Unable to load this Garage vehicle.'),
          );
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [initializing, user?.uid, username, vehicleSlug]);

  if (loading) {
    const loadingContent = (
      <View style={styles.loading}>
        <ActivityIndicator color={colors.primary} />
        <Text style={styles.helperText}>Loading Garage vehicle...</Text>
      </View>
    );

    return user?.uid ? (
      <AppChromeShell fixedBottomTabs>
        <View style={styles.appErrorContent}>{loadingContent}</View>
      </AppChromeShell>
    ) : (
      <PublicPageShell maxWidth={860}>{loadingContent}</PublicPageShell>
    );
  }

  if (error) {
    const errorContent = (
      <ErrorView title="Vehicle unavailable" message={error.message} />
    );

    return user?.uid ? (
      <AppChromeShell fixedBottomTabs>
        <View style={styles.appErrorContent}>{errorContent}</View>
      </AppChromeShell>
    ) : (
      <PublicPageShell maxWidth={860}>{errorContent}</PublicPageShell>
    );
  }

  if (!data?.vehicle) {
    const notFoundContent = user?.uid ? (
      <InternalNotFound message="This Garage vehicle may have moved, been deleted, or is no longer available." />
    ) : (
      <ErrorView
        title="Vehicle unavailable"
        message="This Garage link is not available."
      />
    );

    return user?.uid ? (
      <AppChromeShell fixedBottomTabs>
        <View style={styles.appNotFoundContent}>{notFoundContent}</View>
      </AppChromeShell>
    ) : (
      <PublicPageShell maxWidth={860}>{notFoundContent}</PublicPageShell>
    );
  }

  if (user?.uid) {
    return <SignedInGaragePage initial={data} sourceUsername={username} />;
  }

  const vehicle = data.vehicle;
  const shareURL = garageVehicleShareURL(vehicle);

  function scrollPublicToComments() {
    publicScrollRef.current?.scrollTo({
      animated: true,
      y: Math.max(publicCommentsOffset - spacing.md, 0),
    });
  }

  return (
    <PublicPageShell maxWidth={860} scrollRef={publicScrollRef}>
      <View style={styles.publicContent}>
        <VehicleHeader
          engagement={
            <VehicleEngagement
              onCommentPress={scrollPublicToComments}
              shareURL={shareURL}
              vehicle={vehicle}
            />
          }
          onViewerCommentPress={scrollPublicToComments}
          photos={data.photos}
          shareURL={shareURL}
          vehicle={vehicle}
        />
        <GarageModsPanel vehicle={vehicle} />
        <VehicleComments
          comments={data.comments}
          onSortModeChange={setSortMode}
          onLayout={(event) => {
            setPublicCommentsOffset(event.nativeEvent.layout.y);
          }}
          sortMode={sortMode}
          vehicle={vehicle}
        />
      </View>
    </PublicPageShell>
  );
}

const styles = StyleSheet.create({
  list: {
    flex: 1,
    width: '100%',
  },
  listContent: {
    alignSelf: 'center',
    maxWidth: 860,
    paddingBottom: spacing.xl,
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.md,
    width: '100%',
  },
  signedInContent: {
    flex: 1,
  },
  publicContent: {
    gap: 0,
  },
  loading: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  vehicleCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: spacing.xl,
    overflow: 'hidden',
  },
  coverFrame: {
    aspectRatio: 16 / 9,
    backgroundColor: colors.surfaceTint,
  },
  coverImage: {
    height: '100%',
    width: '100%',
  },
  coverPlaceholder: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  coverPlaceholderText: {
    color: colors.secondary,
    fontSize: 28,
    fontWeight: '900',
  },
  vehicleContent: {
    gap: spacing.sm,
    padding: spacing.md,
  },
  ownerHeader: {
    alignSelf: 'stretch',
  },
  vehicleTitle: {
    color: colors.text,
    fontSize: 28,
    fontWeight: '900',
  },
  subtitle: {
    color: colors.mutedText,
    fontSize: 16,
    fontWeight: '700',
  },
  metaRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  metaText: {
    color: colors.mutedText,
    fontSize: 13,
    fontWeight: '800',
    textTransform: 'capitalize',
  },
  metaDot: {
    color: colors.mutedText,
  },
  storyText: {
    color: colors.text,
    fontSize: 15,
    lineHeight: 22,
  },
  engagementRow: {
    alignItems: 'center',
    borderTopColor: colors.border,
    borderTopWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: spacing.sm,
    paddingTop: spacing.md,
  },
  engagementLeft: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
  },
  commentCount: {
    alignItems: 'center',
    borderRadius: 8,
    flexDirection: 'row',
    gap: spacing.xs,
    minHeight: 36,
    paddingHorizontal: spacing.xs,
  },
  commentButton: {
    justifyContent: 'center',
  },
  commentButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  shareButton: {
    alignItems: 'center',
    borderRadius: 8,
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  shareButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  shareButtonDisabled: {
    opacity: 0.45,
  },
  commentCountText: {
    color: colors.mutedText,
    fontSize: 15,
    fontWeight: '800',
  },
  coverSlider: {
    backgroundColor: colors.surfaceTint,
    flex: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  photoCountPill: {
    backgroundColor: 'rgba(16, 8, 36, 0.72)',
    borderRadius: 999,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    position: 'absolute',
    right: spacing.sm,
    top: spacing.sm,
  },
  photoCountText: {
    color: colors.surface,
    fontSize: 12,
    fontWeight: '900',
  },
  photoDots: {
    alignItems: 'center',
    bottom: spacing.sm,
    flexDirection: 'row',
    gap: spacing.xs,
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
  },
  photoDot: {
    backgroundColor: 'rgba(255, 255, 255, 0.62)',
    borderRadius: 4,
    height: 7,
    width: 7,
  },
  activePhotoDot: {
    backgroundColor: colors.surface,
    width: 18,
  },
  viewerActionCluster: {
    alignItems: 'center',
    gap: 2,
  },
  viewerActionCount: {
    color: colors.surface,
    fontSize: 12,
    fontWeight: '900',
    textShadowColor: 'rgba(0,0,0,0.45)',
    textShadowOffset: { height: 1, width: 0 },
    textShadowRadius: 5,
  },
  viewerActionsRail: {
    alignItems: 'center',
    bottom: 206,
    gap: spacing.sm,
    position: 'absolute',
    right: spacing.md,
    width: 50,
  },
  viewerRailButton: {
    alignItems: 'center',
    gap: 2,
  },
  viewerRailButtonDisabled: {
    opacity: 0.48,
  },
  viewerRailButtonPressed: {
    transform: [{ scale: 0.95 }],
  },
  viewerRailIconCircle: {
    alignItems: 'center',
    backgroundColor: 'rgba(13, 9, 43, 0.62)',
    borderColor: 'rgba(255,255,255,0.12)',
    borderRadius: 25,
    borderWidth: 1,
    height: 50,
    justifyContent: 'center',
    width: 50,
  },
  viewerTopReactionBubble: {
    alignItems: 'center',
    backgroundColor: 'rgba(13, 9, 43, 0.72)',
    borderColor: 'rgba(255,255,255,0.16)',
    borderRadius: 999,
    borderWidth: 1,
    height: 27,
    justifyContent: 'center',
    width: 27,
  },
  viewerTopReactionBubbleOverlap: {
    marginLeft: -8,
  },
  viewerTopReactionButton: {
    alignItems: 'center',
    borderRadius: 999,
    flexDirection: 'row',
    minHeight: 32,
    padding: 2,
  },
  viewerTopReactionEmoji: {
    fontSize: 14,
    lineHeight: 18,
  },
  photoArrow: {
    alignItems: 'center',
    backgroundColor: 'rgba(16, 8, 36, 0.46)',
    borderRadius: 22,
    height: 44,
    justifyContent: 'center',
    marginTop: -22,
    position: 'absolute',
    top: '50%',
    width: 44,
  },
  photoArrowLeft: {
    left: spacing.sm,
  },
  photoArrowRight: {
    right: spacing.sm,
  },
  photoArrowPressed: {
    backgroundColor: 'rgba(16, 8, 36, 0.68)',
  },
  appErrorContent: {
    alignSelf: 'center',
    maxWidth: 860,
    padding: spacing.md,
    width: '100%',
  },
  appNotFoundContent: {
    flex: 1,
  },
  sectionHeader: {
    marginBottom: -spacing.xs,
    paddingHorizontal: spacing.md,
  },
  sectionTitle: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
    lineHeight: 24,
  },
  modsHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
  },
  modsPanel: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    marginBottom: spacing.xl,
    padding: spacing.md,
  },
  modRow: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.xs,
    padding: spacing.md,
  },
  modTitle: {
    color: colors.text,
    fontSize: 16,
    fontWeight: '900',
  },
  modMeta: {
    color: colors.secondary,
    fontSize: 13,
    fontWeight: '800',
  },
  modText: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  commentsHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    justifyContent: 'space-between',
  },
  commentsPanel: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    padding: spacing.sm,
  },
  commentsTitleRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
    minWidth: 0,
  },
  commentCountBadge: {
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 999,
    justifyContent: 'center',
    minHeight: 28,
    minWidth: 38,
    paddingHorizontal: spacing.sm,
  },
  commentCountBadgeEmpty: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderWidth: 1,
  },
  commentSectionCountText: {
    color: colors.surface,
    fontSize: 13,
    fontWeight: '900',
  },
  commentCountTextEmpty: {
    color: colors.secondary,
  },
  emptyState: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.md,
  },
  emptyTitle: {
    color: colors.text,
    fontSize: 17,
    fontWeight: '900',
  },
  emptyText: {
    color: colors.secondary,
    fontSize: 15,
    lineHeight: 22,
  },
  heading: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  commentModalAuthor: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
  },
  commentModalBody: {
    color: colors.secondary,
    fontSize: 15,
    lineHeight: 21,
  },
  commentModalPreview: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
  },
  commentModalPreviewAvatar: {
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderRadius: 20,
    height: 40,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 40,
  },
  commentModalPreviewAvatarImage: {
    height: '100%',
    width: '100%',
  },
  commentModalPreviewAvatarText: {
    color: colors.secondary,
    fontSize: 16,
    fontWeight: '900',
  },
  commentModalPreviewText: {
    flex: 1,
    gap: spacing.xs,
    minWidth: 0,
  },
  comment: {
    alignItems: 'flex-start',
    borderTopColor: colors.border,
    borderTopWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    paddingTop: spacing.lg,
  },
  commentGroup: {
    gap: spacing.sm,
  },
  commentReply: {
    borderTopWidth: 0,
    marginLeft: spacing.xl,
    paddingLeft: spacing.sm,
    paddingTop: spacing.sm,
  },
  commentAvatar: {
    alignItems: 'center',
    backgroundColor: colors.accentSoft,
    borderRadius: 20,
    height: 40,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 40,
  },
  commentAvatarImage: {
    height: '100%',
    width: '100%',
  },
  commentAvatarText: {
    color: colors.secondary,
    fontSize: 16,
    fontWeight: '900',
  },
  commentBody: {
    flex: 1,
    gap: spacing.sm,
  },
  commentAuthor: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
  },
  commentAuthorButton: {
    flexShrink: 1,
    minWidth: 0,
  },
  commentAuthorRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
    minWidth: 0,
  },
  commentAuthorUsername: {
    color: colors.mutedText,
    flexShrink: 1,
    fontSize: 14,
  },
  commentHeader: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.sm,
    justifyContent: 'space-between',
  },
  commentHeaderText: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  commentHeaderActions: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.xs,
  },
  commentText: {
    color: colors.text,
    fontSize: 15,
    lineHeight: 22,
  },
  commentMedia: {
    aspectRatio: 1,
    backgroundColor: colors.surfaceTint,
    borderRadius: 8,
    maxWidth: 320,
    overflow: 'hidden',
    width: '100%',
  },
  commentMediaImage: {
    height: '100%',
    width: '100%',
  },
  commentMediaWrap: {
    gap: spacing.xs,
  },
  giphyAttribution: {
    color: colors.mutedText,
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  commentActions: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
    justifyContent: 'space-between',
    minHeight: 36,
  },
  commentActionLeft: {
    alignItems: 'center',
    flexWrap: 'wrap',
    flexDirection: 'row',
    gap: spacing.xs,
  },
  commentAge: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '800',
    minWidth: 28,
  },
  commentReactionSummary: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    minHeight: 32,
  },
  reactionCountText: {
    marginLeft: -4,
  },
  commentReplyButton: {
    borderRadius: 999,
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
  },
  commentReplyButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  commentReplyButtonText: {
    color: colors.secondary,
    fontSize: 12,
    fontWeight: '900',
  },
  deleteCommentButton: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 34,
    justifyContent: 'center',
    width: 34,
  },
  deleteCommentButtonDisabled: {
    opacity: 0.45,
  },
  deleteCommentButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  editCommentButton: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    height: 34,
    justifyContent: 'center',
    width: 34,
  },
  editCommentButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  commentEditedButton: {
    alignItems: 'center',
    borderRadius: 999,
    flexDirection: 'row',
    gap: 3,
    minHeight: 28,
    paddingHorizontal: 2,
  },
  commentEditedButtonPressed: {
    opacity: 0.78,
  },
  commentEditedText: {
    color: colors.primary,
    fontSize: 12,
    fontWeight: '900',
  },
  signInCta: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.lg,
  },
  signInTitle: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
  },
  signInText: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  helperText: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 22,
  },
});
